import React, { useEffect } from 'react';
import Navigation from './components/Navigation';
import DemoModal from './components/DemoModal';
import Hero from './sections/Hero';
import ProductDemo from './sections/ProductDemo';
import Features from './sections/Features';
import Industries from './sections/Industries';
import AISection from './sections/AISection';
import Security from './sections/Security';
import WebApps from './sections/WebApps';
import Performance from './sections/Performance';
import WhyPitboard from './sections/WhyPitboard';
import Testimonials from './sections/Testimonials';
import FAQ from './sections/FAQ';
import ContactSection from './sections/ContactSection';
import Pricing from './sections/Pricing';
import SiteFooter from './sections/SiteFooter';
import { SITE_META, LEGAL } from './constants';
import { LanguageProvider, useLanguage } from './LanguageContext';
import './PitboardSite.css';

// ─── Contenu principal — langue injectée depuis le Provider ──────────────────
function PitboardSiteContent() {
  const { lang } = useLanguage();
  const meta     = SITE_META[lang];

  // ── Polices Google Fonts ──
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=Inter:wght@300;400;500&display=swap';
    document.head.appendChild(link);
    return () => { if (document.head.contains(link)) document.head.removeChild(link); };
  }, []);

  // ── SEO : titre, meta description, keywords, Open Graph, canonical, JSON-LD ──
  useEffect(() => {
    const prevTitle   = document.title;
    const mDesc       = document.querySelector('meta[name="description"]');
    const mOgTitle    = document.querySelector('meta[property="og:title"]');
    const mOgDesc     = document.querySelector('meta[property="og:description"]');
    const prevDesc    = mDesc    ? mDesc.getAttribute('content')    : '';
    const prevOgTitle = mOgTitle ? mOgTitle.getAttribute('content') : '';
    const prevOgDesc  = mOgDesc  ? mOgDesc.getAttribute('content')  : '';

    document.title = meta.title;
    document.documentElement.lang = lang;
    if (mDesc) mDesc.setAttribute('content', meta.description);

    let mKeywords = document.querySelector('meta[name="keywords"]');
    const kwCreated = !mKeywords;
    if (kwCreated) {
      mKeywords = document.createElement('meta');
      mKeywords.name = 'keywords';
      document.head.appendChild(mKeywords);
    }
    mKeywords.setAttribute('content', meta.keywords);

    if (mOgTitle) mOgTitle.setAttribute('content', meta.title);
    if (mOgDesc)  mOgDesc.setAttribute('content',  meta.description);

    // ── Favicon PitBoard ──
    const favicon32 = document.querySelector('link[sizes="32x32"]');
    const favicon16 = document.querySelector('link[sizes="16x16"]');
    const prevFav32 = favicon32 ? favicon32.getAttribute('href') : null;
    const prevFav16 = favicon16 ? favicon16.getAttribute('href') : null;
    if (favicon32) favicon32.setAttribute('href', '/pitboard-favicon.svg');
    if (favicon16) favicon16.setAttribute('href', '/pitboard-favicon.svg');

    let canonical = document.getElementById('pb-canonical');
    const canonCreated = !canonical;
    if (canonCreated) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.id  = 'pb-canonical';
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', meta.siteUrl);

    const prevLd = document.getElementById('pb-jsonld-main');
    if (prevLd && prevLd.parentNode) prevLd.parentNode.removeChild(prevLd);

    const jsonLd = document.createElement('script');
    jsonLd.type  = 'application/ld+json';
    jsonLd.id    = 'pb-jsonld-main';
    jsonLd.text  = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'SoftwareApplication',
          name: 'PitBoard Suite',
          applicationCategory: 'BusinessApplication',
          applicationSubCategory: lang === 'fr' ? 'Logiciel de gestion assurance' : 'Insurance management software',
          description: meta.description,
          operatingSystem: 'Web, PWA',
          url: meta.siteUrl,
          inLanguage: lang,
          countryOfOrigin: 'FR',
          featureList: lang === 'fr'
            ? ['CRM assurance', 'CRM courtier', 'ERP assurance', "Logiciel d'assistance", 'Plateforme assurance']
            : ['Insurance CRM', 'Broker CRM', 'Insurance ERP', 'Assistance management', 'Insurance platform'],
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', availability: 'https://schema.org/OnlineOnly' },
          provider: { '@type': 'Organization', name: LEGAL.company, url: meta.siteUrl },
        },
        {
          '@type': 'Organization',
          name: LEGAL.company,
          url: meta.siteUrl,
          contactPoint: { '@type': 'ContactPoint', email: LEGAL.email, contactType: 'customer service', availableLanguage: lang },
          address: { '@type': 'PostalAddress', streetAddress: '11 Rue Urbain IV', addressLocality: 'Troyes', postalCode: '10000', addressCountry: 'FR' },
        },
      ],
    });
    document.head.appendChild(jsonLd);

    return () => {
      document.title = prevTitle;
      document.documentElement.lang = 'fr';
      if (mDesc)    mDesc.setAttribute('content',    prevDesc);
      if (mOgTitle) mOgTitle.setAttribute('content', prevOgTitle);
      if (mOgDesc)  mOgDesc.setAttribute('content',  prevOgDesc);
      if (prevFav32 && favicon32) favicon32.setAttribute('href', prevFav32);
      if (prevFav16 && favicon16) favicon16.setAttribute('href', prevFav16);
      if (kwCreated    && mKeywords.parentNode)  mKeywords.parentNode.removeChild(mKeywords);
      if (canonCreated && canonical.parentNode)  canonical.parentNode.removeChild(canonical);
      const ld = document.getElementById('pb-jsonld-main');
      if (ld && ld.parentNode) ld.parentNode.removeChild(ld);
    };
  }, [lang, meta]);

  // ── Scroll reveal ──
  useEffect(() => {
    const sections = document.querySelectorAll('.pb-section');
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('pb-in-view'); }); },
      { threshold: 0.06 }
    );
    sections.forEach(s => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="pitboard-site" id="accueil" data-testid="pitboard-site-page">
      <Navigation />
      <DemoModal />
      <main>
        <Hero />
        <ProductDemo />
        <div className="pb-sep" />
        <Features />
        <div className="pb-sep" />
        <Industries />
        <AISection />
        <div className="pb-sep" />
        <Security />
        <div className="pb-sep" />
        <WebApps />
        <div className="pb-sep" />
        <Performance />
        <div className="pb-sep" />
        <Pricing />
        <WhyPitboard />
        <div className="pb-sep" />
        <Testimonials />
        <div className="pb-sep" />
        <FAQ />
        <ContactSection />
      </main>
      <SiteFooter />
    </div>
  );
}

export default function PitboardSitePage() {
  return (
    <LanguageProvider>
      <PitboardSiteContent />
    </LanguageProvider>
  );
}
